import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';
import { States } from "../../shared/config/constant.module";
import template from './jobRunReport.tpl.html';
import { Plant } from "../../models/navigation";
import { apsViewModels } from "../../shared/dto/APS.ViewModels";
import { StateParams } from "../../common";
import LoaderServiceName, { LoaderService } from "../../components/loader/loader.service";
import ReportServiceName, { ReportService } from "../../services/reports/report.service";
import DateUtilityService from "../../services/utility/dateutility.service";
import NavigationServiceName, { NavigationService } from "../../services/navigation/navigation.service";
import { TopDefectsReportParams } from "./topDefectsReport.controller";
import { apsEnums } from "../../shared/dto/aps.enums";
import { DowntimeReportParams } from "./downtimeReport.controller";

let requires = [LoaderServiceName, ReportServiceName, NavigationServiceName];

interface JobRunReportController {
  requirePlantSelection: boolean;
  plants: Plant[];
  selectedPlant: Plant;
  selectedShop: apsViewModels.ShopNavigation;
  selectedMoldName: string;
  startDate: Date;
  endDate: Date;
  selectedJobRun: apsViewModels.JobRunReportItem;
  form: angular.IFormController;

  results: apsViewModels.JobRunReportItem[];
  detailsResults: apsViewModels.JobRunReportDetailsItem;
  searchPerformed: boolean;

  molds: string[];
  moldsLoading: boolean;
  refreshMolds(): void;

  generateReport(): void;
  getJobRunReportDetails(jobRun: apsViewModels.JobRunReportItem): void;

  formatTime(time: Date, timeZoneId: string): string;
  goToTopDefectsReport(defectTypeId: string): void;
  goToDowntimeReport(): void;
  groupByArea(): void;
}

interface JobRunParams extends StateParams {
  reportPlantId: string;
  reportShopId: string;
  moldName: string;
  startDate: string;
  endDate: string;
  jobRunId: string;
  autoRun: boolean;
}

class JobRunReportCtrl implements JobRunReportController {
  requirePlantSelection: boolean;
  plants: Plant[];
  selectedPlant: Plant;
  selectedShop: apsViewModels.ShopNavigation;
  selectedMoldName: string;
  startDate: Date;
  endDate: Date;
  selectedJobRun: apsViewModels.JobRunReportItem;
  form: angular.IFormController;

  results: apsViewModels.JobRunReportItem[];
  detailsResults: apsViewModels.JobRunReportDetailsItem;
  searchPerformed: boolean;

  molds: string[];
  moldsLoading: boolean;

  loaderService: LoaderService;
  reportService: ReportService;
  $state: angular.ui.IStateService;
  groupedResults: object;
  $anchorScroll: angular.IAnchorScrollService;
  $location: angular.ILocationService;
  private $q: angular.IQService;
  private $stateParams: JobRunParams;

  constructor(
    navigationService: NavigationService,
    reportService: ReportService,
    loaderService: LoaderService,
    $anchorScroll: angular.IAnchorScrollService,
    $location: angular.ILocationService,
    $q: angular.IQService,
    $stateParams: JobRunParams,
    $state: angular.ui.IStateService) {

    this.loaderService = loaderService;
    this.reportService = reportService;
    this.$anchorScroll = $anchorScroll;
    this.$location = $location;
    this.$state = $state;
    this.$q = $q;
    this.$stateParams = $stateParams;

    this.requirePlantSelection = navigationService.getCurrentRole().roleRequiresPlants;

    var today = moment().startOf('day');
    this.endDate = today.toDate();
    this.startDate = today.clone().subtract(1, 'month').toDate();
    this.molds = [];

    var promise = navigationService.getNavigation().then((nav) => {
      this.plants = nav.plants();
      this.selectedPlant = nav.selectedPlant;
      this.selectedShop = nav.selectedShop;

      return this.loadState($stateParams);
    });

    loaderService.loadingPromise(promise);
  }

  private loadState(params: JobRunParams): angular.IPromise<any> {
    if (params.reportPlantId && params.reportShopId && params.reportShopId !== params.shopId) {
      var plant = _.find(this.plants, { id: params.reportPlantId });
      if (plant) {
        var shop = _.find(plant.shops, { id: params.reportShopId });
        if (shop) {
          this.selectedPlant = plant;
          this.selectedShop = shop;
        }
      }
    }

    if (params.moldName) {
      this.selectedMoldName = params.moldName;
    }

    if (params.startDate) {
      var startDate = moment(params.startDate);
      if (startDate.isValid()) {
        this.startDate = startDate.toDate();
      }
    }

    if (params.endDate) {
      var endDate = moment(params.endDate);
      if (endDate.isValid()) {
        this.endDate = endDate.toDate();
      }
    }

    if (!params.autoRun) {
      return this.$q.when(true);
    }

    return this.runReport().then(() => {
      if (!params.jobRunId) {
        return this.$q.when(true);
      }

      var jobRun = _.find(this.results, { id: params.jobRunId });
      if (!jobRun) {
        return this.$q.when(true);
      }

      this.selectedJobRun = jobRun;
      return this.loadJobRunReport(jobRun.id);
    });
  }

  exportToCsv() {

    return _.orderBy(_.map<apsViewModels.JobRunReportItem, any>(this.results, (x) => {
      return {
        from: this.formatTime(x.startDate, x.shopPlantTimeZoneId),
        to: this.formatTime(x.endDate, x.shopPlantTimeZoneId) || 'Running',
        days: x.duration.toString(),
        shop: x.shopName,
        mold: x.jobMold,
        finish: x.jobFinish,
        description: x.jobDescription,
        sects: x.numberOfSections,
        gobs: x.numberOfGobs,
        averagePackPercentage: x.averagePackPercentage
      };
    }), ['to'], ['desc']);
  }
  groupByArea = function () {
    let groupedResults = {};

    this.detailsResults.downtimeAreas.forEach(function (item) {
      if (!groupedResults[item.area]) {
        groupedResults[item.area] = {
          area: item.area,
          reasons: [],
          totalDowntime: 0 
        };
      }
      // Add each reason and downtimeTotal under the specific area
      groupedResults[item.area].reasons.push({
        reason: item.reason,
        downtimeTotal: item.downtimeTotal
      });
      groupedResults[item.area].totalDowntime += item.downtimeTotal;
    });

    this.groupedResults = groupedResults;
  };
  private runReport(): angular.IPromise<any> {
    var startDateStart = moment(this.startDate).startOf('day').toDate();
    var endDateEnd = moment(this.endDate).endOf('day').toDate();

    if (this.selectedPlant) {
      startDateStart = DateUtilityService.localToTimeZone(startDateStart, this.selectedPlant.timeZoneId).toDate();
      endDateEnd = DateUtilityService.localToTimeZone(endDateEnd, this.selectedPlant.timeZoneId).toDate();
    }

    var searchCriteria = {
      plantId: this.selectedPlant ? this.selectedPlant.id : null,
      shopId: this.selectedShop ? this.selectedShop.id : null,
      moldName: this.selectedMoldName,
      startDate: startDateStart,
      endDate: endDateEnd
    };

    return this.reportService
      .getJobRunReport(searchCriteria)
      .then(reportItems => {
        this.results = reportItems;
        this.searchPerformed = true;
      });
  }

  private saveState() {
    var reportShopId = this.selectedShop ? this.selectedShop.id : undefined;
    if (reportShopId === this.$stateParams.shopId) {
      reportShopId = undefined;
    }
    var params: JobRunParams = {
      plantId: this.$stateParams.plantId,
      shopId: this.$stateParams.shopId,
      userId: this.$stateParams.userId,
      reportPlantId: (reportShopId && this.selectedPlant) ? this.selectedPlant.id : undefined,
      reportShopId: reportShopId,
      moldName: this.selectedMoldName,
      startDate: this.startDate ? this.startDate.toISOString() : undefined,
      endDate: this.endDate ? this.endDate.toISOString() : undefined,
      jobRunId: this.selectedJobRun ? this.selectedJobRun.id : undefined,
      autoRun: true
    };

    this.$state.transitionTo(this.$state.current.name, params);
  }

  generateReport() {
    if (this.form.$invalid) {
      return;
    }

    this.saveState();

    this.detailsResults = null;

    this.loaderService.loadingPromise(this.runReport());
  }

  refreshMolds() {
    var plantId = this.selectedPlant ? this.selectedPlant.id : null;
    var shopId = this.selectedShop ? this.selectedShop.id : null;

    this.molds = [];
    this.moldsLoading = true;
    this.reportService.getJobRunReportMolds(plantId, shopId).then((results: string[]) => {
      this.molds = results;
      this.moldsLoading = false;
    });
  }

  private loadJobRunReport(jobRunId: string): angular.IPromise<any> {
    return this.reportService.getJobRunReportDetails(jobRunId)
      .then(reportDetails => {
        (<any>reportDetails).checkTypes = [
          { name: 'Line Checks', checks: reportDetails.lineChecks, numDecimals: 1 },
          { name: 'Gob Checks', checks: reportDetails.gobChecks, numDecimals: 2 }
        ];
        this.detailsResults = reportDetails;
        this.groupByArea();
        this.$location.hash('detailResults');
        this.$anchorScroll();
      });
  }

  getJobRunReportDetails(jobRun: apsViewModels.JobRunReportItem): void {
    this.selectedJobRun = jobRun;
    this.saveState();
    this.loaderService.loadingPromise(this.loadJobRunReport(jobRun.id));
  }

  formatTime(time: Date, timeZoneId: string): string {
    return DateUtilityService.formatTime(time, timeZoneId);
  }

  goToTopDefectsReport(defectTypeId: string): void {
    var params: TopDefectsReportParams = {
      plantId: this.$stateParams.plantId,
      shopId: this.$stateParams.shopId,
      userId: this.$stateParams.userId,
      reportPlantId: this.$stateParams.shopId !== this.selectedJobRun.shopId
        ? this.selectedJobRun.shopPlantId : undefined,
      reportShopId: this.$stateParams.shopId !== this.selectedJobRun.shopId
        ? this.selectedJobRun.shopId : undefined,
      startDate: this.selectedJobRun.startDate.toISOString(),
      endDate: this.selectedJobRun.endDate ? this.selectedJobRun.endDate.toISOString() : undefined,
      defectTypeId: defectTypeId,
      filter: apsEnums.TopDefectsFilter.Top10Defects,
      autoRun: true
    };
    this.$state.go(States.TOP_DEFECTS_REPORT, params);
  }

  goToDowntimeReport(): void {
    var params: DowntimeReportParams = {
      plantId: this.$stateParams.plantId,
      shopId: this.$stateParams.shopId,
      userId: this.$stateParams.userId,
      reportPlantId: this.$stateParams.shopId !== this.selectedJobRun.shopId
        ? this.selectedJobRun.shopPlantId : undefined,
      reportShopId: this.$stateParams.shopId !== this.selectedJobRun.shopId
        ? this.selectedJobRun.shopId : undefined,
      startDate: this.selectedJobRun.startDate.toISOString(),
      endDate: this.selectedJobRun.endDate ? this.selectedJobRun.endDate.toISOString() : undefined,
      type: apsEnums.DowntimeType.All,
      location: apsEnums.DowntimeLocation.All,
      orderBy: apsEnums.DowntimeOrder.Time,
      showAll: 'false',
      autoRun: true
    };
    this.$state.go(States.DOWNTIME_REPORT, params);
  }
}

export default angular.module('aps.controller.jobRunReport', requires)
  .config(function ($stateProvider: angular.ui.IStateProvider) {
    $stateProvider.state({
      name: States.JOB_RUN_REPORT,
      url: '/admin/reports/jobRun?startDate&endDate&jobRunId&moldName&autoRun&reportPlantId&reportShopId',
      params: {
        startDate: { dynamic: true },
        endDate: { dynamic: true },
        jobRunId: { dynamic: true },
        moldName: { dynamic: true },
        autoRun: { dynamic: true },
        reportPlantId: { dynamic: true },
        reportShopId: { dynamic: true },
      },
      controller: 'JobRunReportCtrl as vm',
      template: template,
      data: {
        pageTitle: 'Production Report'
      }
    });
  })
  .controller('JobRunReportCtrl', JobRunReportCtrl)
  .name;



// WEBPACK FOOTER //
// ./src/app/controllers/reports/jobRunReport.controller.ts